import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useEventList } from '../contexts/EventListProvider';
import { Event } from '../types';

const SESSION_KEY = 'eventSplashSeen';

const formatDate = (iso: string): string => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    });
};

const getNextUpcoming = (events: Event[]): Event | null => {
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = events
        .filter(e => (e.endDate ?? e.date) >= today)
        .sort((a, b) => a.date.localeCompare(b.date));
    return upcoming[0] ?? null;
};

const EventSplashModal: React.FC = () => {
    const { eventList, loading } = useEventList();
    const [visible, setVisible] = useState(false);
    const [event, setEvent] = useState<Event | null>(null);

    useEffect(() => {
        if (loading) return;
        if (sessionStorage.getItem(SESSION_KEY)) return;
        const next = getNextUpcoming(eventList);
        if (!next) return;
        setEvent(next);
        setVisible(true);
    }, [loading, eventList]);

    const handleClose = () => setVisible(false);

    const handleViewAll = () => {
        sessionStorage.setItem(SESSION_KEY, 'true');
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && event && (
                <motion.div
                    key="splash"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {/* Backdrop */}
                    <Box
                        data-testid="splash-backdrop"
                        onClick={handleClose}
                        sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(7,11,28,0.92)' }}
                    />

                    {/* Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.35, ease: 'easeOut', delay: 0.05 }}
                        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480, margin: '0 16px' }}
                    >
                        <Box
                            data-testid="splash-card"
                            sx={{ backgroundColor: '#0f1929', outline: '1px solid #c9a84c', outlineOffset: '4px' }}
                        >
                            <img
                                src={event.image}
                                alt={event.title}
                                style={{ width: '100%', display: 'block' }}
                            />
                            <Box sx={{ p: 3 }}>
                                <Box sx={{ width: 32, height: 2, backgroundColor: '#c9a84c', mb: 2 }} />
                                <Typography
                                    data-testid="splash-title"
                                    variant="h5"
                                    sx={{ fontFamily: '"Cormorant Garamond", serif', mb: 0.5 }}
                                >
                                    {event.title}
                                </Typography>
                                <Typography
                                    data-testid="splash-date"
                                    variant="body2"
                                    sx={{ color: '#9e9e9e', fontFamily: '"Inter", sans-serif', mb: 0.5 }}
                                >
                                    {formatDate(event.date)}
                                </Typography>
                                {event.location && (
                                    <Typography
                                        data-testid="splash-location"
                                        variant="body2"
                                        sx={{ color: '#9e9e9e', fontFamily: '"Inter", sans-serif', mb: 2 }}
                                    >
                                        {event.location}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        component={RouterLink}
                                        to="/events"
                                        onClick={handleViewAll}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            borderColor: '#c9a84c',
                                            color: '#c9a84c',
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: '0.75rem',
                                            letterSpacing: '0.08em',
                                            '&:hover': { borderColor: '#f0ede8', color: '#f0ede8' },
                                        }}
                                    >
                                        See All Events
                                    </Button>
                                    <Button
                                        data-testid="splash-close-btn"
                                        onClick={handleClose}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            borderColor: 'rgba(158,158,158,0.4)',
                                            color: '#9e9e9e',
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: '0.75rem',
                                            letterSpacing: '0.08em',
                                            '&:hover': { borderColor: '#f0ede8', color: '#f0ede8' },
                                        }}
                                    >
                                        Close
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </motion.div>

                    {/* Close button — fixed top-right, matching ArtDetailModal */}
                    <IconButton
                        data-testid="splash-close"
                        onClick={handleClose}
                        sx={{
                            position: 'fixed',
                            top: 16,
                            right: 16,
                            color: '#9e9e9e',
                            zIndex: 2,
                            '&:hover': { color: '#f0ede8' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default EventSplashModal;
